import styled from '@emotion/styled'
import { css } from '@emotion/react'

import { Page } from '../../retail/elements'

/* Match the 800px breakpoint used by the Page components extended here. */
const UPSELL_MOBILE_VIEWPORT = '(max-width: 800px)'
export const UPSELL_MOBILE = `@media ${UPSELL_MOBILE_VIEWPORT}`

/* Below 356px a card can't clear a 70px icon plus a two-line title. */
const LARGE_ICON_MIN_VIEWPORT_PX = 356
const UPSELL_NARROW_VIEWPORT = `(max-width: ${LARGE_ICON_MIN_VIEWPORT_PX - 0.01}px)`

export const UPSELL_LARGE_ICON_VIEWPORT = `${UPSELL_MOBILE_VIEWPORT} and (min-width: ${LARGE_ICON_MIN_VIEWPORT_PX}px)`

const FONT_STACK = "'Open Sans', 'Helvetica Neue', sans-serif"

/* Fits the longest card copy while preserving its padding. */
const CARD_SIZE = '140px'

/* Match the 150px mobile design without letting cards grow on tablets. */
const MOBILE_CARD_MAX_SIZE = '150px'

/* Limit the tablet layout to the desktop text-column width. */
const MOBILE_CONTENT_MAX_WIDTH = '500px'

/* Account for the 16px padding already supplied by Main's content wrapper. */
export const PageShell = styled(Page.WelcomeContent)`
  margin-top: 29px;
  font-family: ${FONT_STACK};

  ${UPSELL_MOBILE} {
    margin-top: 14px;
    max-width: ${MOBILE_CONTENT_MAX_WIDTH};
    padding: 0 4px;
  }
`

export const MainRow = styled.div`
  display: flex;
  align-items: center;
  gap: 90px;

  ${UPSELL_MOBILE} {
    flex-direction: column;
    align-items: stretch;
    gap: 30px;
  }
`

export const Intro = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 500px;

  ${UPSELL_MOBILE} {
    align-items: center;
    max-width: none;
    text-align: center;
  }
`

export const Heading = styled.h1`
  margin: 0;
  color: ${p => p.theme.mediumGray};
  font-size: 45px;
  font-weight: 400;
  line-height: 50px;

  ${UPSELL_MOBILE} {
    color: ${p => p.theme.darkestGray};
    font-size: 24px;
    line-height: 30px;
  }
`

export const BodyCopy = styled.p`
  margin: 20px 0 0;
  color: ${p => p.theme.mediumGray};
  font-size: 16px;
  line-height: 22px;

  ${UPSELL_MOBILE} {
    display: none;
  }
`

export const PriceRow = styled(Page.PlanPrice)`
  margin-top: 30px;

  & .currency,
  & .amount {
    font-size: 28px;
  }

  & .interval {
    font-size: 22px;
  }
`

export const CheckoutButton = styled(Page.PlanCta)`
  margin-top: 28px;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`

export const CheckoutError = styled(Page.ErrorMessage)`
  margin: 10px 0 0;
  font-size: 12px;
  line-height: 16px;
`

export const Disclaimer = styled.div`
  margin-top: 20px;
  color: ${p => p.theme.lightestGray};
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
`

export const ContactArea = styled.div`
  ${Page.ContactRow} {
    flex-direction: row-reverse;
    gap: 55px;
  }

  ${Page.ContactHeading} {
    font-size: 30px;
  }
`

export const CardGrid = styled.div`
  flex: none;
  display: grid;
  grid-template-columns: repeat(2, ${CARD_SIZE});
  gap: 20px;

  ${UPSELL_MOBILE} {
    grid-template-columns: repeat(2, minmax(0, ${MOBILE_CARD_MAX_SIZE}));
    justify-content: center;
    gap: 24px;
  }
`

export const CardOuter = styled.button`
  box-sizing: border-box;
  width: ${CARD_SIZE};
  height: ${CARD_SIZE};
  padding: 0;
  border: none;
  border-radius: 10px;
  background: transparent;
  perspective: 1000px;
  cursor: pointer;
  font-family: ${FONT_STACK};

  ${UPSELL_MOBILE} {
    width: 100%;
    height: auto;
    aspect-ratio: 1;
  }
`

export const CardInner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  transform: rotateY(${p => (p.$flipped ? '180deg' : '0deg')});
  transform-style: preserve-3d;
  transition: transform 0.6s;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const cardFace = p => css`
  position: absolute;
  inset: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 10px;
  background: ${p.theme.cardBgColor ?? '#ffffff'};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  backface-visibility: hidden;
  color: ${p.theme.mediumGray};
  font-size: 14px;
  font-weight: 400;
  line-height: 19px;
  text-align: center;
  overflow-wrap: break-word;
`

export const CardFront = styled.div`
  ${cardFace};
  gap: 6px;
  transition: background 0.2s;

  @media (hover: hover) {
    ${CardOuter}:hover & {
      background: ${p => p.theme.cardHoverBgColor ?? '#f5fafc'};
    }
  }
`

/* Caps the label width so every card title wraps to the design's two lines. */
export const CardLabel = styled.span`
  max-width: 90px;
`

export const CardBack = styled.div`
  ${cardFace};
  transform: rotateY(180deg);
`

/* Prevent a long word from sizing the flex item beyond the card. */
export const CardDetail = styled.span`
  max-width: 100%;
  /* Honor the copy's soft hyphen only on the narrowest cards. */
  hyphens: none;

  @media ${UPSELL_NARROW_VIEWPORT} {
    hyphens: manual;
  }
`
